// src/stores/useWfQueueStore.js
//
// The WellFinder adjudication working set: an ordered list of record ids, an
// id-keyed cache of the list-row payloads behind them, and the cursor that
// extends it.
//
// A sibling store rather than a slice of useWellFinderStore. That store owns
// map selection for the WellFinder product; this owns a review queue that has
// no map of its own in 3a. Of the ~40 stores here only one composes slices, so
// a flat sibling is the consistent shape.
//
// Established in 3a even though 3a only lists. 3c advances through records one
// at a time and needs an ordered working set with a cache in front of it;
// retrofitting that later is much more expensive than starting with it.

import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { toast } from "./useToastStore";
import {
  REVIEW_ZOOM, DETAIL_ZOOM, defaultZoomFor,
} from "../components/wellfinder/reviewMapEncoding";

// Module-level constants, NOT `|| []` in selector bodies. A fresh literal in a
// selector returns a new reference every render, which Zustand reads as a state
// change, which re-renders, which returns a new reference. This codebase has
// been bitten by exactly that loop before.
export const EMPTY_ARRAY = Object.freeze([]);
export const EMPTY_OBJECT = Object.freeze({});

const DEFAULT_FILTERS = Object.freeze({
  tiers: EMPTY_ARRAY,
  statuses: Object.freeze(["pending"]),
  sort: "thorough",
  // The operating AOI, or null for the whole queue. TRANSIENT BY DESIGN: this
  // store has no persist, so every session starts unfiltered, exactly like the
  // tier chips. If reviewers complain about reselecting, that complaint is the
  // signal to persist it — not this comment.
  aoiId: null,
});

const PAGE_SIZE = 50;

// SEQ IS A STRING. wellfinder_adjudication_events.seq is bigint, and node-pg
// serialises bigint as a string to avoid silent precision loss past 2^53 — so
// event.seq arrives as "5016", not 5016. Anything sorting or comparing it must
// coerce: Number(a.seq) - Number(b.seq), never a.seq - b.seq (NaN) and never
// a.seq > b.seq (lexicographic, so "9" > "10"). The server already returns
// events in seq order, so 3a needs no client-side sort; this note exists so the
// first person who adds one does not introduce the bug.
/**
 * Is this candidate field populated enough to adopt from?
 *
 * Empty string is absent — the registry writes '' where it has nothing, and
 * trim() is what stops a whitespace value being adopted as a real one.
 */
export function adoptable(cand, key) {
  return typeof cand?.[key] === "string" && cand[key].trim() !== "";
}

/**
 * The rank an approve adopts: the one selected, else the candidate that IS the
 * record's match (tiers a and a′ arrive pre-matched to rank 1), else none.
 *
 * NOT rank 1 by default. It was, here and in the verdict bar, so de-selecting a
 * candidate and approving adopted rank 1 anyway, and a tier B approve adopted a
 * well nobody had chosen. With nothing to adopt and candidates in range, an
 * approve asks (candidatePrompt) rather than guessing.
 */
export function adoptRank(detail, selectedRank) {
  if (selectedRank != null) return selectedRank;
  const rrcId = detail?.record?.rrc_id;
  if (!rrcId) return null;
  return detail?.candidates?.find((c) => c.rrc_id === rrcId)?.rank ?? null;
}

/**
 * The two 409s, told apart.
 *
 * They arrive with the same status and mean opposite things. Collapsing them
 * into one message would leave the analyst guessing whether to skip or reload,
 * and both answers are wrong half the time.
 */
export function shapeCommitError(err) {
  // ── KEY ON THE SERVER'S CODE, NOT THE HTTP STATUS ──────────────────────────
  // A 403 was falling through to the generic branch, which renders err.message
  // — the raw "Forbidden" — and offers "Reload and re-decide". That is the
  // STALE-TOKEN affordance: a permissions failure was being routed through the
  // concurrency path, and reloading will never fix it. The server has always
  // sent a code for exactly this reason; nothing was reading it.
  if (err?.code === "not_adjudication_reviewer") {
    return {
      kind: "not_reviewer",
      message: err.payload?.detail || err.message,
      requiredRole: err.payload?.required_role || "wellfinder_reviewer",
    };
  }
  if (err?.code === "not_adjudication_org") {
    return { kind: "not_org", message: err.payload?.detail || err.message };
  }
  // ── THE TRANSFER'S REFUSALS, BEFORE THE GENERIC 409s ───────────────────────
  // holder_changed and holder_stale carry rrc_id too, so they must be read
  // before the rrc_taken branch below or they would render as "already
  // matched" — the one message that is certainly wrong for them.
  if (err?.code === "holder_changed" || err?.code === "holder_stale") {
    return { kind: "holder_changed", message: err.message };
  }
  if (err?.code === "holder_escalated") {
    return { kind: "holder_escalated", message: err.message };
  }
  // Two writes met — a deadlock, or a lock wait past the server's timeout.
  // Read BEFORE the generic 409s: it is not "this record changed" (nothing
  // about it did) and it is not rrc_taken. Reload and try again resolves it.
  if (err?.code === "contention") {
    return { kind: "contention", message: err.message };
  }
  if (err?.code === "record_claimed_by_other") {
    return {
      kind: "claimed_by_other",
      message: err.message,
      holder: err.payload?.claimed_by_name ?? "another analyst",
      heldForSeconds: err.payload?.held_for_seconds ?? null,
      // Set when the claim is on the record the well was being taken FROM,
      // not on this one — the words differ, and skipping this record would
      // not resolve it.
      onHolder: err.payload?.holder_record_id != null,
    };
  }
  if (err?.status === 409 && err?.payload?.rrc_id) {
    // A THIRD 409. Neither skip nor reload helps: the registry well the analyst
    // chose is already bound to another record, and the resolution is to pick a
    // different candidate or reject as a duplicate. Adjacent detections
    // routinely share a rank-1, so this will be common.
    return {
      kind: "rrc_taken",
      message: err.message,
      rrcId: err.payload.rrc_id,
      conflictingRecordId: err.payload.conflicting_record?.id ?? null,
      conflictingTier: err.payload.conflicting_record?.tier ?? null,
    };
  }
  if (err?.status === 409) {
    return {
      kind: "stale",
      message: err.message,
      expected: err.payload?.expected_updated_at ?? null,
      actual: err.payload?.actual_updated_at ?? null,
    };
  }
  return { kind: "other", message: err?.message ?? "Commit failed" };
}

export const seqNum = (e) => Number(e?.seq);

/**
 * Ascending by seq, coercing first. The server already returns events in seq
 * order so this is not needed today — it exists because the moment anyone
 * merges an optimistic local event into the list they will reach for a sort,
 * and `a.seq - b.seq` is NaN while `a.seq > b.seq` puts "9" after "10".
 */
export const bySeq = (a, b) => seqNum(a) - seqNum(b);

function buildQueueUrl({ tiers, statuses, sort, aoiId }, cursor) {
  const p = new URLSearchParams();
  p.set("limit", String(PAGE_SIZE));
  p.set("sort", sort);
  for (const t of tiers) p.append("tier", t);
  for (const s of statuses) p.append("status", s);
  // Omitted entirely when null, rather than sent empty. The server reads absent
  // as unfiltered; an empty string would be a uuid parse error.
  if (aoiId) p.set("operating_aoi_id", aoiId);
  // The cursor is opaque. It is base64url of a server-internal tuple that
  // includes a tier ordinal RELATIVE TO THE CHOSEN SORT, so it is only
  // meaningful alongside the sort it was issued under. Passed back verbatim,
  // never parsed, never constructed here.
  if (cursor) p.set("cursor", cursor);
  return `/api/v1/wellfinder/queue?${p.toString()}`;
}

const CLAIM_BATCH = 20;
// Claim more when the unworked tail gets this short. Chosen so a claim round
// trip never lands between two records: at three seconds each, five records is
// fifteen seconds of runway for a call that takes well under one.
const CLAIM_LOW_WATER = 5;

async function postJson(url, body) {
  const r = await fetchWithAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  let payload = null;
  try { payload = await r.json(); } catch { /* non-JSON error body */ }
  if (r.ok) return payload;
  const err = new Error(payload?.error || `Request failed (${r.status})`);
  err.status = r.status;
  err.code = payload?.code ?? null;
  err.payload = payload ?? null;
  throw err;
}

/** fetchWithAuth returns a raw Response and there is no 403 interceptor. */
async function getJson(url) {
  const r = await fetchWithAuth(url);
  if (r.ok) return r.json();

  let body = null;
  try { body = await r.json(); } catch { /* non-JSON error body */ }
  const err = new Error(body?.error || `Request failed (${r.status})`);
  err.status = r.status;
  // The server's DreamHammer gate. Surfaced by code so the UI can distinguish
  // "wrong org" from a generic denial without matching on prose.
  err.code = body?.code ?? null;
  err.detail = body?.detail ?? null;
  throw err;
}

// Commit's own status for a verdict, for when the response omits it.
const VERDICT_TO_STATUS = Object.freeze({ approved: "approved", rejected: "rejected", flagged: "needs_review" });

// ── WRITE GENERATIONS: A LOAD THAT STARTED BEFORE A WRITE CANNOT OUTLIVE IT ──
//
// Bumped for a record every time this client writes to it (commit, reopen,
// undo, or taking its well in a move). A load captures the generation when it
// starts and may repaint or cache what it fetched only if the generation is
// unchanged when it returns. The review loop runs at about three seconds a
// record, so a background refresh fired on arrival routinely lands AFTER the
// commit — carrying the pre-commit record, which would otherwise go straight
// back into the cache the write just cleared. Module scope, not store state:
// it is bookkeeping for in-flight requests, nothing renders from it.
const writeGen = new Map();
const genOf = (id) => writeGen.get(id) ?? 0;

export const useWfQueueStore = create((set, get) => ({
  // ── Working set ────────────────────────────────────────────────────────────
  ids: EMPTY_ARRAY,
  records: EMPTY_OBJECT,
  cursor: null,
  hasMore: true,
  loading: false,
  error: null,

  filters: DEFAULT_FILTERS,

  // ── Selection ──────────────────────────────────────────────────────────────
  //
  // An id, not a record. The row payload already lives in `records`, and holding
  // a second copy here would let the two drift the moment a page refresh
  // replaces the cached row. It is also the seam 3b consumes: the detail view
  // takes an id and fetches its own, richer payload, so anything more than an id
  // here would be a shape 3b has to ignore.
  //
  // Null is a real state and the initial one. An empty queue has nothing to
  // select, and auto-selecting the first row on load would fire 3b's fetch for a
  // record the analyst never asked for.
  selectedId: null,

  // ── Detail (3b) ────────────────────────────────────────────────────────────
  //
  // Keyed by the record id it belongs to, so a slow response for record A
  // cannot land after the analyst has advanced to B and paint A's candidates
  // under B's map. At three seconds a record that race is not hypothetical.
  detail: null,
  detailId: null,
  detailLoading: false,
  detailError: null,

  // Candidate highlight, in the store rather than in the panel because it is
  // BIDIRECTIONAL — the map sets it on hover and reads it for paint, and the
  // panel does the same. Component-local state would need one of them to own it
  // and the other to be told, which is the shape that produces the flicker.
  //
  // hoveredRank is transient; selectedRank survives the pointer leaving. In 3c
  // selectedRank becomes the adopt target, which is why it is a separate field
  // from the hover and not a merged "active".
  hoveredRank: null,
  selectedRank: null,

  // Set when an approve found candidates in range and nothing to adopt: the
  // verdict bar asks for a candidate or Approved – No Candidate. Cleared by
  // choosing either, and by moving to another record.
  candidatePrompt: false,

  // Approved – No Candidate, armed by 0 and waiting for the equipment: p, f or
  // a then commits it, as each commits a normal approve. Cleared by choosing a
  // candidate, by Esc, by moving to another record and by the commit.
  noCandidateArmed: false,

  // Which taken candidate's HOLDER is on the map, by rank. Separate from
  // selectedRank: showing who holds a well is looking, not choosing an adopt
  // target, and the two must not move each other. Per record — select()
  // clears it, because rank 3 on one record is a different well on the next.
  holderRank: null,

  // ── Moving a well here from the record that holds it ──────────────────────
  //
  // Approving with a TAKEN candidate selected does not post; it opens this, and
  // approving again confirms. A deliberate detour, like reopen — the analyst is
  // overriding another record's claim, so the bar says whose and what happens
  // to it before anything is written. `opts` is the approve that opened it
  // (equipment type), or null when the chip opened it and no approve has been
  // chosen yet.
  //
  // transferNote is SEPARATE from noteDraft: that is this record's verdict
  // note; this one is recorded on the OTHER record, as the reason its match
  // was taken, and is required when that record carries a verdict.
  transferConfirm: null,    // { rank, opts, needNote }
  transferNote: "",

  // SEPARATE from detailError. Imagery is an enrichment: a tile-token failure
  // must not blank the record pane, because the candidates, the facts and the
  // history are all still readable and adjudicable-from. It surfaces as a
  // legible overlay on the map and nothing else.
  imageryError: null,

  // Null when claims are being taken normally. A string when claimBatch is
  // refusing to claim, so the queue header can SAY SO — see claimBatch for why
  // a silent no-op here was the whole problem.
  claimsPausedReason: null,

  // ── Commit (3c) ────────────────────────────────────────────────────────────
  committing: false,

  // Shaped, not raw. The two 409s mean completely different things and the UI
  // has to tell them apart: one is "someone else is on it, skip", the other is
  // "it moved, reload and re-decide". A single error string cannot express that.
  commitError: null,        // { kind, message, holder, heldFor, ... }

  // Preserved ACROSS a failed commit. Losing a Tier C note to a 409 costs five
  // minutes of review, which is the whole point of not using dialogs.
  noteDraft: "",
  rejectPickerOpen: false,
  // Same shape as rejectPickerOpen: a destructive action that needs an input
  // reveals the input when it is CHOSEN, not before. A required note field
  // sitting on every adjudicated record asks the analyst for a reason before
  // they have decided there is one.
  reopenOpen: false,
  helpOpen: false,

  // Detail zoom. NULL means "whatever this record's default is" —
  // defaultZoomFor() picks z20 when rank-1 is close enough to stay on screen
  // and z18 otherwise. A number here is a manual override for the current
  // record only; advance clears it, so no record inherits the previous one's
  // zoom.
  zoomOverride: null,

  // Point move. An explicit mode: an accidental drag inside a three-second loop
  // would silently corrupt geometry, so nothing is draggable until asked.
  geomMode: false,
  geomDraft: null,          // [lon, lat] while moving, null otherwise

  // ── Boundary correction ────────────────────────────────────────────────────
  //
  // A SEPARATE MODE FROM geomMode, not a variant of it. The point says where the
  // well is; the boundary says how big the model thought it was. A reviewer
  // fixes either without the other, and the two cannot be active at once — a
  // draggable point marker and four draggable vertex markers on the same 27 m²
  // of map would be indistinguishable under the cursor.
  boundaryMode: false,
  boundaryDraft: null,      // GeoJSON geometry while editing, null otherwise
  // Set by revertBoundary. The draft below is for DISPLAY only in that state —
  // what actually goes to the server is the action, and the server reads the
  // geometry from the observation.
  boundaryRevert: false,
  boundaryDraftFor: null,   // the record a staged draft was drawn on
  // True while the staged draft is a NEW boundary rather than a correction to
  // one. The two write different assertion types and mean different things
  // about the model — a create is a miss, an edit is an inaccuracy — so the
  // client has to say which it meant rather than let the server guess.
  boundaryCreate: false,

  // The last committed record, for the undo toast. Cleared when it expires so
  // `u` cannot reopen something from ten minutes ago.
  lastCommit: null,         // { recordId, verdict, at }

  // Progress strip. DEFAULT COLLAPSED: it is orientation, read on arrival and
  // when a tier finishes, and it was costing ~210px of viewport at every scroll
  // position on a page whose map has a 480px floor.
  //
  // Same persistence as footprintVisible — module-level store state, so it
  // survives record changes and navigation within the app and resets on a full
  // reload. A preference that resets every navigation is not a preference.
  progressExpanded: false,

  // Footprint visibility. DEFAULT ON, and deliberately NOT reset per record:
  // the toggle exists so an analyst can decide whether ~62 m² of outline helps
  // or clutters, and a setting that snaps back every three seconds cannot
  // answer that. One toggle only — the other layers are not optional.
  footprintVisible: true,

  stats: null,
  statsLoading: false,
  statsError: null,

  reset: () => set({
    ids: EMPTY_ARRAY,
    records: EMPTY_OBJECT,
    cursor: null,
    hasMore: true,
    loading: false,
    error: null,
    // Selection dies with the working set. Keeping it would leave selectedId
    // pointing at a record that is no longer in `ids` after a tier change, and
    // 3b would render a detail panel for a row the analyst cannot see.
    selectedId: null,
    detail: null,
    detailId: null,
    detailLoading: false,
    detailError: null,
    hoveredRank: null,
    selectedRank: null,
    holderRank: null,
    transferConfirm: null,
    transferNote: "",
    imageryError: null,
  }),

  select: (id) => {
    if (get().selectedId === id) return;
    // Candidate highlight belongs to the record being looked at. Carrying rank
    // 3 across to a record with two candidates would highlight nothing and read
    // as a rendering bug.
    set({
      selectedId: id, hoveredRank: null, selectedRank: null, imageryError: null,
      holderRank: null, candidatePrompt: false, noCandidateArmed: false,
      transferConfirm: null, transferNote: "",
      reopenOpen: false, noteDraft: "",
      // Back to the new record's own default on every change.
      zoomOverride: null,
      // ── STAGED GEOMETRY BELONGS TO THE RECORD IT WAS DRAWN ON ────────────
      //
      // Without this a draft SURVIVES the move and is committed against the
      // NEXT record. Observed on dev 2026-09-10: a boundary staged on one
      // record was written as a correction on another, 0.36 degrees and ~34 km
      // away — a 54.8 m2 model polygon replaced by a 1.0 m2 one from somewhere
      // else entirely.
      //
      // Which is the worst failure this feature has. A wrong training label is
      // not a rendering bug someone notices; it is a confident,
      // provenance-stamped assertion that the model erred in a place it was
      // never asked about, and downstream it reads exactly like a correct one.
      //
      // geomMode/geomDraft are cleared for the same reason. That hole was
      // LATENT ON THE POINT PATH before the boundary existed — a staged point
      // move also survived a record change — and had simply never fired,
      // because no point move has ever been committed on this corpus.
      geomMode: false, geomDraft: null,
      boundaryMode: false, boundaryDraft: null, boundaryRevert: false,
      boundaryDraftFor: null, boundaryCreate: false,
    });
    if (id) get().loadDetail(id);
  },

  hoverCandidate: (rank) => {
    if (get().hoveredRank === rank) return;
    set({ hoveredRank: rank });
  },

  selectCandidate: (rank) => {
    // Clicking the selected candidate again clears it. Read-only in 3b; in 3c
    // this is how an analyst backs out of an adopt target without committing.
    // A pending move was for the candidate it named; changing the target
    // withdraws it rather than moving a different well.
    set({ selectedRank: get().selectedRank === rank ? null : rank, transferConfirm: null, candidatePrompt: false,
          noCandidateArmed: false });
  },

  /** From the holder chip: target this candidate and ask how to approve it. */
  openTransfer: (rank) => {
    set({ selectedRank: rank, transferConfirm: { rank, opts: null, needNote: false }, commitError: null });
  },
  cancelTransfer: () => set({ transferConfirm: null, transferNote: "" }),

  // ── A LOCAL COPY DOES NOT OUTLIVE A WRITE ──────────────────────────────────
  // records[id].__detail is the copy a revisit paints instantly. It used to be
  // written once, by prefetchNext, and never again — so returning to a record
  // after committing it painted the PRE-commit record, pending and with its
  // verdict buttons, until a background refresh landed. Every write this
  // client makes drops the copy for each record it touched and bumps its
  // generation (see writeGen).
  _dropPrefetch: (ids) => set((s) => {
    const records = { ...s.records };
    for (const id of ids) {
      if (!id) continue;
      writeGen.set(id, genOf(id) + 1);
      if (records[id]?.__detail) {
        const { __detail: _stale, ...row } = records[id];
        records[id] = row;
      }
    }
    return { records };
  }),
  // The queue row carries status too, and the list showed "pending" for a
  // record just approved. Rows not in the loaded working set are left alone.
  _setRowStatus: (id, status) => set((s) => (
    id && s.records[id] ? { records: { ...s.records, [id]: { ...s.records[id], status } } } : {}
  )),
  _cacheDetail: (id, d) => set((s) => (
    s.records[id] ? { records: { ...s.records, [id]: { ...s.records[id], __detail: d } } } : {}
  )),
  setTransferNote: (v) => set({ transferNote: v }),

  // Same toggle shape as selectCandidate: showing the shown one hides it.
  toggleHolder: (rank) => {
    set({ holderRank: get().holderRank === rank ? null : rank });
  },

  setImageryError: (err) => set({ imageryError: err }),

  toggleFootprint: () => set((s) => ({ footprintVisible: !s.footprintVisible })),

  toggleProgressExpanded: () => set((s) => ({ progressExpanded: !s.progressExpanded })),

  setNoteDraft: (v) => set({ noteDraft: v }),
  setRejectPickerOpen: (v) => set({ rejectPickerOpen: v, commitError: null }),
  setReopenOpen: (v) => set({
    reopenOpen: v, commitError: null,
    // Closing discards the half-typed reason: it belongs to the reopen the
    // analyst backed out of, not to whatever they do next.
    noteDraft: v ? get().noteDraft : "",
  }),
  setHelpOpen: (v) => set({ helpOpen: v }),
  clearCommitError: () => set({ commitError: null }),

  setGeomMode: (v) => set({
    geomMode: v,
    geomDraft: v ? get().geomDraft : null,
    // Mutually exclusive — see the note on boundaryMode.
    ...(v ? { boundaryMode: false, boundaryDraft: null } : {}),
  }),

  /**
   * Enter or leave boundary editing.
   *
   * Entering seeds the draft from the EFFECTIVE geometry — the correction if one
   * exists, the model's polygon otherwise — so a second reviewer refines what
   * the first drew instead of starting from the model and silently discarding
   * their work.
   */
  setBoundaryMode: (v) => {
    const st = get();
    if (v && !st.detail?.footprint?.boundary_editable) return;
    set({
      boundaryMode: v,
      boundaryDraft: v ? (st.boundaryDraft ?? st.detail?.footprint?.geometry ?? null) : null,
      ...(v ? { boundaryRevert: false } : { boundaryCreate: false }),
      ...(v ? { geomMode: false, geomDraft: null } : {}),
    });
  },

  // Dragging a corner is an EDIT, whatever preceded it — so a reviewer who
  // reverts and then adjusts is committing a correction again, not a revert.
  /**
   * Start a boundary where the model drew none, from a SEEDED BOX.
   *
   * WHY A BOX AND NOT A DRAWING TOOL. The editor drags four corners; a create
   * has none to drag. Seeding a square and letting the reviewer nudge it reuses
   * the entire interaction — no drawing library, no second gesture to learn,
   * and no MlDraw on a map whose own comment records rejecting it for
   * installing map-wide handlers that fight the candidate bindings.
   *
   * SIZE COMES FROM THE CORPUS, not from taste: every well_head_boundary is a
   * rectangle and the mean area is 26.9 m2, so the seed is a 5.2 m square —
   * about what the model draws — centred on the detection point. A reviewer
   * adjusts a plausible box rather than inventing one from nothing.
   *
   * The longitude degree is scaled by cos(lat) so the seed is SQUARE ON THE
   * GROUND rather than square in degrees; at 31.7N the un-scaled version would
   * be 15% wider than it is tall.
   */
  seedBoundaryDraft: () => {
    const st = get();
    const fp = st.detail?.footprint;
    if (!fp?.boundary_creatable) return;
    // The same expression ReviewMap:137 uses to place the detection marker, so
    // the seed is centred on the point the reviewer is looking at rather than
    // on a differently-derived one. `effective` is the adjusted position when a
    // reviewer has moved it, which is the right centre — a boundary drawn
    // around where the well ISN'T would be a worse label than none.
    const pt = st.detail?.geometry?.effective?.coordinates
            ?? st.detail?.geometry?.detection?.coordinates
            ?? null;
    if (!Array.isArray(pt) || pt.length < 2) return;

    const HALF_M = 2.6;                       // 5.2 m square ~= 26.9 m2
    const dLat = HALF_M / 111320;
    const dLon = HALF_M / (111320 * Math.cos((pt[1] * Math.PI) / 180));
    const [lon, lat] = pt;
    const ring = [
      [lon - dLon, lat - dLat],
      [lon + dLon, lat - dLat],
      [lon + dLon, lat + dLat],
      [lon - dLon, lat + dLat],
      [lon - dLon, lat - dLat],
    ];
    set({
      boundaryMode: true,
      boundaryCreate: true,
      boundaryRevert: false,
      boundaryDraft: { type: "MultiPolygon", coordinates: [[ring]] },
      boundaryDraftFor: st.detail?.record?.id ?? null,
      geomMode: false,
      geomDraft: null,
    });
  },

  setBoundaryDraft: (geometry) => set({
    boundaryDraft: geometry,
    boundaryRevert: false,
    // boundaryCreate is NOT cleared: dragging the seeded box is still creating
    // a boundary, not correcting one. Only leaving the mode or changing record
    // ends a create.
    
    // WHICH RECORD IT WAS DRAWN ON. select() clears the draft, so this should
    // never disagree — it is the second lock, because the first one silently
    // failing is exactly what wrote a polygon onto another record's boundary.
    boundaryDraftFor: get().detail?.record?.id ?? null,
  }),

  /**
   * Stage the model's ORIGINAL polygon as the draft.
   *
   * THE ONLY SAFE UNDO. feature_assertions is append-only, so a retraction is a
   * new geometry_edit — and the obvious way to make one, redrawing by hand, is
   * the trap: the corpus reads the LATEST edit, so an approximation of the
   * model's own polygon becomes a label asserting the model was wrong when it
   * was right. Nothing downstream can tell that from a genuine correction.
   *
   * So this stages model_geometry VERBATIM, straight from the server, with no
   * rounding, no reprojection and no pass through the vertex markers. Byte
   * identity is the whole point; an eyeballed revert is the defect.
   */
  revertBoundary: () => {
    const model = get().detail?.footprint?.model_geometry ?? null;
    if (!model) return;
    // The draft is staged so the map SHOWS the model's shape immediately. It is
    // not what gets committed: the payload carries action:'revert' and no
    // geometry, because model_geometry has already been through ST_AsGeoJSON at
    // 9 decimal places and cannot be reparsed back into the original float.
    // Sending it would write a label off by 7.4e-14 degrees — nothing on the
    // ground, and still a label asserting the model was wrong.
    set({ boundaryDraft: model, boundaryRevert: true, boundaryMode: false });
  },

  /**
   * Flip to the other zoom. Relative to what is CURRENTLY shown, not to a fixed
   * "in/out" — the two are different records' defaults now, and a toggle that
   * always meant "go to z20" would be a no-op on records that already open
   * there.
   */
  toggleZoom: () => {
    const { zoomOverride, detail } = get();
    const current = zoomOverride ?? defaultZoomFor(detail);
    set({ zoomOverride: current === DETAIL_ZOOM ? REVIEW_ZOOM : DETAIL_ZOOM });
  },
  setGeomDraft: (lonlat) => set({ geomDraft: lonlat }),

  /** Esc. One place, so every mode has the same escape. */
  cancelMode: () => set({
    geomMode: false, geomDraft: null,
    boundaryMode: false, boundaryDraft: null, boundaryRevert: false,
    boundaryDraftFor: null, boundaryCreate: false,
    rejectPickerOpen: false,
    reopenOpen: false, noteDraft: "",
    transferConfirm: null, transferNote: "",
    helpOpen: false, commitError: null, zoomOverride: null, candidatePrompt: false,
    noCandidateArmed: false,
  }),

  // 0 arms No Candidate; a second 0 disarms. Only where there are candidates
  // to decline, as the server refuses it elsewhere.
  armNoCandidate: () => {
    const st = get();
    if (!st.detail?.candidates?.length) return;
    set({ noCandidateArmed: !st.noCandidateArmed, candidatePrompt: false, commitError: null });
  },

  // ── Claims ─────────────────────────────────────────────────────────────────
  //
  // Claim on entry and top up when the tail runs short. `loadNextPage(trigger)`
  // was built in 3a and documented as deliberately unused; this is what it was
  // for.
  claimBatch: async (limit = CLAIM_BATCH) => {
    const { filters } = get();

    // ── ONLY CLAIM ON THE ORDINARY PENDING QUEUE ─────────────────────────────
    //
    // A claim reserves work you are ABOUT TO DO. The server's claim query is
    // hardcoded to `r.status = 'pending'` and takes no status from the body, so
    // claiming while viewing any other status reserves twenty records the
    // analyst is not looking at.
    //
    // That was not a cosmetic bug. A supervisor opening the flagged queue
    // (?status=needs_review) silently claimed twenty PENDING records — taking
    // them off other analysts' queues for the full TTL — and claimBatch then
    // merged them into the working set, where they landed AHEAD of the ten
    // escalations that were actually asked for.
    //
    // The guard lives here rather than at the two call sites so a third caller
    // cannot reintroduce it.
    if (filters.statuses.join(",") !== "pending") {
      // SILENT WAS THE BUG, NOT THE GUARD. Refusing is correct — the server's
      // claim query is hardcoded to pending, and claiming while viewing another
      // status reserves twenty records the analyst is not looking at. But
      // returning 0 with no trace meant an analyst who touched the status chips
      // lost claim protection for the rest of the session with no way to know.
      //
      // Measured on dev before this was written: ZERO calls to POST
      // /queue/claim across 20 minutes in which 193 commits landed, and
      // wellfinder_record_claims sampled 0 rows throughout. With nothing
      // reserving a record between the queue list and the commit, two analysts
      // in one tier walk the same ordered list and the second to commit is told
      // the record is already decided — which reads as "someone else got there
      // first" and is the symptom analysts reported.
      set({
        claimsPausedReason:
          "Claiming is paused while a status filter other than Pending is active. "
          + "Records you open are not reserved, so another analyst may commit them first.",
      });
      return 0;
    }

    try {
      const d = await postJson("/api/v1/wellfinder/queue/claim", {
        limit,
        tier: filters.tiers.length ? filters.tiers : undefined,
        sort: filters.sort,
        // THE AOI, OR THE FILTER LEAKS THROUGH THE CLAIM PATH. The server
        // selects in queue order when this is absent, so a reviewer filtered to
        // an AOI gets twenty records from outside it merged into `ids` — the
        // set `advance` walks. Measured before this was sent: 15 of 20 claimed
        // records fell outside the Oxy AOI; with it, 0 of 20.
        //
        // Same reason `status` is sent above: a claim endpoint that cannot see
        // what queue the caller is looking at cannot refuse a claim that makes
        // no sense.
        operating_aoi_id: filters.aoiId ?? undefined,
        // SENT SO THE SERVER CAN DISAGREE. The guard above already prevents a
        // non-pending claim, so this is never anything but ['pending'] today —
        // which is the point: the endpoint used to have no way to tell what
        // queue the caller was looking at, so it could not refuse a claim that
        // made no sense. Stating it means a future caller that loses the guard
        // gets a 400 instead of silently claiming twenty records.
        status: filters.statuses,
      });
      const claimed = d?.items ?? [];
      set({ claimsPausedReason: null });
      if (!claimed.length) return 0;
      // Merge into the working set so advance has somewhere to go. Claimed
      // records may already be on the loaded page; ids stays unique.
      set((s) => {
        const seen = new Set(s.ids);
        const fresh = claimed.filter((i) => !seen.has(i.id));
        const records = { ...s.records };
        for (const i of claimed) records[i.id] = { ...records[i.id], ...i };
        return {
          ids: fresh.length ? [...s.ids, ...fresh.map((i) => i.id)] : s.ids,
          records,
        };
      });
      return claimed.length;
    } catch (err) {
      // Best effort. A failed claim costs contention, not correctness — the
      // soft enforcement server-side means an unclaimed commit still succeeds.
      console.warn("[wf] claim failed", err);
      return 0;
    }
  },

  releaseClaims: async () => {
    try { await postJson("/api/v1/wellfinder/queue/release", {}); }
    catch { /* best effort; the TTL is the real guarantee */ }
  },

  /**
   * Prefetch the NEXT record's detail while the analyst is on the current one.
   * At three seconds a round trip between records is the whole budget.
   */
  prefetchNext: async () => {
    const { ids, selectedId, records } = get();
    const i = ids.indexOf(selectedId);
    const nextId = i >= 0 ? ids[i + 1] : null;
    if (!nextId || records[nextId]?.__detail) return;
    const gen = genOf(nextId);
    try {
      const d = await getJson(`/api/v1/wellfinder/records/${nextId}`);
      // Dropped if the record was written while this was in flight — a move
      // can change the next record, as the holder, before it is reached.
      if (genOf(nextId) === gen) get()._cacheDetail(nextId, d);
    } catch { /* prefetch is an optimisation; the real load will retry */ }
  },

  /** Move to the next record, topping up claims when the tail is short. */
  advance: async () => {
    const { ids, selectedId } = get();
    const i = ids.indexOf(selectedId);
    const remaining = i >= 0 ? ids.length - i - 1 : ids.length;
    if (remaining <= CLAIM_LOW_WATER) await get().claimBatch();
    get().moveSelection(1);
    get().prefetchNext();
  },

  // ── Commit ─────────────────────────────────────────────────────────────────
  /**
   * The one write. Builds the body from the CURRENT record and the caller's
   * verdict; the client never sends a value, only an instruction.
   *
   * @param {'approved'|'rejected'|'flagged'} verdict
   * @param {{equipmentType?: string|null, rejectReason?: string|null,
   *           flagReason?: string|null, noCandidate?: boolean}} opts
   *   noCandidate: Approved – No Candidate. None of the candidates is this
   *   well: sends match.action 'no_candidate' and adopts nothing. Its
   *   equipment is stated as a normal approve's is: equipmentType, or none
   *   for "unclear", which it sends as equipment_unclear (the server refuses
   *   a no_candidate that states neither).
   */
  commit: async (verdict, { equipmentType = null, rejectReason = null, flagReason = null, noCandidate = false } = {}) => {
    const st = get();
    const detail = st.detail;
    const rec = detail?.record;
    if (!rec || st.committing) return null;
    const hasCandidates = (detail.candidates?.length ?? 0) > 0;
    // A statement about the candidates, so there must be some; the server
    // refuses it on tier C, and the button is disabled there.
    if (noCandidate && (verdict !== "approved" || !hasCandidates)) return null;

    const rank = noCandidate ? null : adoptRank(detail, st.selectedRank);
    const cand = detail.candidates?.find((c) => c.rank === rank) ?? null;

    // NOTHING TO ADOPT, AND CANDIDATES IN RANGE: ASK. The server refuses this
    // approve (400 match_required); asking first costs the reviewer nothing.
    if (verdict === "approved" && !noCandidate && hasCandidates && !cand) {
      set({ candidatePrompt: true, commitError: null });
      return null;
    }

    // ── A TAKEN CANDIDATE: CONFIRM FIRST, THEN SEND take_from ────────────────
    // The first approve opens the confirmation; an approve while it is open
    // for this candidate confirms, with that approve's equipment type. The
    // holder and its version come from the candidate as loaded — what the
    // analyst saw — so a holder that has moved on is refused, not overridden.
    let takeFrom = null;
    if (verdict === "approved" && cand?.is_taken) {
      const pending = st.transferConfirm;
      const opts = { equipmentType, rejectReason, flagReason };
      if (!pending || pending.rank !== rank) {
        set({ transferConfirm: { rank, opts, needNote: false }, commitError: null });
        return null;
      }
      const note = st.transferNote.trim();
      if (cand.taken_by_status !== "pending" && !note) {
        set({ transferConfirm: { ...pending, opts, needNote: true } });
        return null;
      }
      takeFrom = {
        record_id: cand.taken_by_record_id,
        expected_updated_at: cand.taken_by_updated_at,
        ...(note ? { note } : {}),
      };
    }

    const attributes = {};
    if (verdict === "approved" && cand) {
      // ADOPT IS AN INSTRUCTION, NOT A VALUE. The server reads the candidate
      // itself; sending a value would let a stale client write a number the
      // registry no longer holds. Enforced server-side — body.provenance is
      // never read — and this is the client half of the same contract.
      attributes.api10 = { action: "adopt", candidate_rank: rank };
      // Only where the source field is actually populated. An adopt against an
      // empty field would be {source:'rrc'} on a null — a provenance-stamped
      // assertion that the registry knows nothing, which reads as data.
      if (adoptable(cand, "rrc_status_norm")) {
        attributes.well_status = { action: "adopt", candidate_rank: rank };
      }
      if (adoptable(cand, "rrc_oil_gas_code")) {
        attributes.oil_gas_code = { action: "adopt", candidate_rank: rank };
      }
    }
    // Manual provenance ALWAYS. equipment_type comes from the imagery, and the
    // registry has no equipment concept to adopt from.
    if (equipmentType) attributes.equipment_type = { action: "manual", value: equipmentType };

    const body = {
      expected_updated_at: rec.updated_at,
      verdict,
      ...(rejectReason ? { reject_reason: rejectReason } : {}),
      // Required by the server when the verdict is 'flagged', and rejected by
      // it on any other verdict — so this is conditional for the same reason
      // reject_reason above is, not as a convenience.
      ...(flagReason ? { flag_reason: flagReason } : {}),
      ...(st.noteDraft.trim() ? { note: st.noteDraft.trim() } : {}),
      ...(verdict === "approved" && cand
        ? { match: { action: "adopt", candidate_rank: rank, ...(takeFrom ? { take_from: takeFrom } : {}) } }
        : noCandidate
          ? { match: { action: "no_candidate", ...(equipmentType ? {} : { equipment_unclear: true }) } }
          : {}),
      ...(Object.keys(attributes).length ? { attributes } : {}),
      ...(st.geomDraft
        ? { geometry: { action: "move", lon: st.geomDraft[0], lat: st.geomDraft[1] } }
        : {}),
      // Sent only when it DIFFERS from what is already effective. Committing an
      // unchanged polygon would write a geometry_edit assertion asserting a
      // correction nobody made — noise in the corpus the model trains from, and
      // indistinguishable there from a real one.
      // A REVERT SENDS NO GEOMETRY. The server copies it from the observation,
      // which is the only way it can be exact — see revertBoundary. Gated on a
      // committed correction existing, because the server refuses a revert with
      // nothing to revert and the UI must not offer what the server refuses.
      ...(st.boundaryRevert && st.detail?.footprint?.boundary_corrected
        ? { boundary: { action: "revert" } }
        : st.boundaryCreate && st.boundaryDraft && st.boundaryDraftFor === rec.id
          ? { boundary: { action: "create", geometry: st.boundaryDraft } }
        : st.boundaryDraft
            && !st.boundaryRevert
            // Never send a polygon drawn on a DIFFERENT record. Belt to
            // select()'s braces: this lock cannot be defeated by forgetting to
            // clear something.
            && st.boundaryDraftFor === rec.id
            && JSON.stringify(st.boundaryDraft) !== JSON.stringify(st.detail?.footprint?.geometry)
          ? { boundary: { action: "edit", geometry: st.boundaryDraft } }
          : {}),
    };

    set({ committing: true, commitError: null });
    try {
      const res = await postJson(`/api/v1/wellfinder/records/${rec.id}/commit`, body);
      set({
        committing: false,
        noteDraft: "",
        rejectPickerOpen: false,
        geomMode: false,
        geomDraft: null,
        boundaryMode: false,
        boundaryDraft: null,
        boundaryRevert: false,
        boundaryDraftFor: null,
        boundaryCreate: false,
        transferConfirm: null,
        transferNote: "",
        candidatePrompt: false,
        noCandidateArmed: false,
        lastCommit: {
          recordId: rec.id, verdict, at: Date.now(),
          // Set when this commit took the well from another record, so undo
          // gives it back (undo-transfer) instead of reopening this record
          // alone, which kept the well and left the holder with nothing.
          transfer: res?.transfer
            ? {
              holderRecordId: res.transfer.holder_record_id,
              // The id the holder's own header shows — what the toast names.
              holderLabel: cand?.taken_by_detection_source_id ?? cand?.taken_by_source_id ?? "the other record",
              holderStatusBefore: res.transfer.holder_status_before,
            }
            : null,
        },
      });
      // Drop what this commit made stale, BEFORE advance prefetches anything:
      // this record, and the holder when the commit took its well.
      get()._dropPrefetch([rec.id, res?.transfer?.holder_record_id]);
      get()._setRowStatus(rec.id, res?.status ?? VERDICT_TO_STATUS[verdict]);
      if (res?.transfer) get()._setRowStatus(res.transfer.holder_record_id, "pending");
      get().advance();
      return res;
    } catch (err) {
      // NOTHING IS CLEARED. noteDraft, geomDraft and the selected candidate all
      // survive, because both 409s are recoverable and the analyst's input is
      // the expensive part — a Tier C note is five minutes of work.
      set({ committing: false, commitError: shapeCommitError(err) });
      return null;
    }
  },

  /** Reopen the currently-open record. Requires a note; the server 400s without. */
  reopenCurrent: async (note) => {
    const rec = get().detail?.record;
    if (!rec || !note?.trim()) return null;
    try {
      const res = await postJson(`/api/v1/wellfinder/records/${rec.id}/reopen`, {
        expected_updated_at: rec.updated_at,
        note: note.trim(),
      });
      set({ noteDraft: "", reopenOpen: false });
      get()._dropPrefetch([rec.id]);
      get()._setRowStatus(rec.id, "pending");
      get().loadDetail(rec.id);
      return res;
    } catch (err) {
      set({ commitError: shapeCommitError(err) });
      return null;
    }
  },

  /**
   * Undo. Reopen is the honest undo: it withdraws the verdict, KEEPS the values
   * and their provenance, downgrades the published correlation, and leaves an
   * event. A silent delete would be a lie about a record having been touched.
   *
   * The note is supplied automatically. Reopen requires one, and prompting for
   * it would defeat a five-second affordance — so it says plainly what
   * happened, which is true and satisfies the constraint.
   */
  undoLastCommit: async () => {
    const lc = get().lastCommit;
    // SILENCE IS WHY THIS LOOKED BROKEN. Pass 1 pressed `u` on records whose
    // commits had all 403'd, so lastCommit was never set and this returned null
    // without a word — indistinguishable from undo being wired up wrong. There
    // is always feedback now.
    if (!lc) {
      toast.info("Nothing to undo — the last commit either failed or has expired.");
      return null;
    }
    const tr = lc.transfer ?? null;
    try {
      // ── A MOVE IS UNDONE BY GIVING THE WELL BACK ─────────────────────────
      // Reopen keeps rrc_id, so reopening alone after a move left this record
      // holding a well it no longer vouched for and the holder with nothing.
      // undo-transfer reverses both halves in one transaction; plain commits
      // still undo through reopen.
      const res = tr
        ? await postJson(`/api/v1/wellfinder/records/${lc.recordId}/undo-transfer`, {
          note: "Undone by the reviewer immediately after commit, from the review queue.",
        })
        : await (async () => {
          const d = await getJson(`/api/v1/wellfinder/records/${lc.recordId}`);
          return postJson(`/api/v1/wellfinder/records/${lc.recordId}/reopen`, {
            expected_updated_at: d.record.updated_at,
            note: "Undone by the reviewer immediately after commit, from the review queue.",
          });
        })();
      // ── BACK TO THE RECORD THROUGH select(), NOT BESIDE IT ─────────────────
      //
      // This used to set selectedId itself, which skipped select()'s reset:
      // commit A, advance to B, stage something on B, press `u` — and B's
      // geometry, boundary draft and note were still staged on A, and B's
      // selectedRank was A's adopt target. The 2026-09-10 cross-record class
      // (a boundary written ~34 km from where it was drawn) by a third door.
      //
      // Drop A's prefetched copy first. It was fetched BEFORE the commit, and
      // loadDetail prefers a prefetch — so the undo painted the pre-commit
      // record, old updated_at and all, and a re-commit before the background
      // refresh landed 409'd.
      const { selectedId } = get();
      // After an undone move the holder changed too: it has the well back.
      get()._dropPrefetch([lc.recordId, tr?.holderRecordId]);
      get()._setRowStatus(lc.recordId, "pending");
      set({ lastCommit: null });
      if (selectedId === lc.recordId) {
        // Committing the last loaded row cannot advance — moveSelection
        // clamps — so the analyst is still on A, anything staged is staged on
        // A, and select(A) would be a no-op that skips the reload. Reload only.
        get().loadDetail(lc.recordId);
      } else {
        // Blank BEFORE select(): select() -> loadDetail may fill detail at
        // once, and B's candidates must not sit under A while it loads.
        set({ detail: null });
        get().select(lc.recordId);
      }
      if (!tr) toast.info("Verdict withdrawn. Values and provenance kept.");
      else if (tr.holderStatusBefore === "pending") {
        toast.info(`Verdict withdrawn; the well is back with ${tr.holderLabel}.`);
      } else {
        // Decision 1a: the holder's verdict is not restored, so it says so.
        toast.info(`Verdict withdrawn; the well is back with ${tr.holderLabel}, which stays open for re-review.`);
      }
      return res;
    } catch (err) {
      if (tr && err?.code === "transfer_moved_on") {
        toast.error(`Can't undo the move: ${tr.holderLabel} has changed since. Reopen this record and review both.`);
      } else {
        toast.error(`Could not undo: ${err.message}`);
      }
      return null;
    }
  },

  loadDetail: async (id) => {
    if (!id) return;
    // Prefetched by the previous record's advance. Using it is what removes the
    // round trip from the three-second loop — without this the prefetch is
    // wasted work.
    const gen = genOf(id);
    // What a load returns is kept — cached for the next visit, and painted if
    // the analyst is still here — only if nothing was written to the record
    // since it started. Otherwise it is the pre-write record: load again.
    const fresh = (d) => {
      if (genOf(id) !== gen) {
        if (get().detailId === id) get().loadDetail(id);
        return false;
      }
      get()._cacheDetail(id, d);
      return true;
    };
    const pre = get().records[id]?.__detail;
    if (pre) {
      set({ detailId: id, detail: pre, detailLoading: false, detailError: null, imageryError: null });
      // Still refresh in the background: expected_updated_at from a stale
      // prefetch would 409 on commit, which is exactly the error this loop must
      // not manufacture for itself. The refreshed copy is kept for next time.
      getJson(`/api/v1/wellfinder/records/${id}`)
        .then((d) => { if (fresh(d) && get().detailId === id) set({ detail: d }); })
        .catch(() => { /* the prefetched copy is still usable */ });
      return;
    }
    set({ detailId: id, detailLoading: true, detailError: null, imageryError: null });
    try {
      const d = await getJson(`/api/v1/wellfinder/records/${id}`);
      // Dropped if the analyst has already moved on. See the note by `detail`.
      if (get().detailId !== id) { fresh(d); return; }
      if (!fresh(d)) return;
      set({ detail: d, detailLoading: false });
    } catch (err) {
      if (get().detailId !== id) return;
      set({ detailLoading: false, detailError: err, detail: null });
    }
  },

  /**
   * Move the selection by `delta` within the loaded working set.
   *
   * Clamps rather than wrapping. Wrapping from the last row to the first reads
   * as a glitch when the list is 50 rows out of 10,803 — the analyst has not
   * reached the end of anything, only the end of what is loaded. Clamping at the
   * bottom is also where 3c hangs load-more-then-advance.
   *
   * With nothing selected, the first press selects the first row (or the last,
   * moving up), which is what makes the keyboard usable without a click first.
   */
  moveSelection: (delta) => {
    const { ids, selectedId } = get();
    if (!ids.length) return;
    if (selectedId == null) {
      get().select(delta > 0 ? ids[0] : ids[ids.length - 1]);
      return;
    }
    const i = ids.indexOf(selectedId);
    // Selected id not in the current list: treat as unselected rather than
    // computing from -1, which would jump to the wrong end.
    if (i === -1) { get().select(ids[0]); return; }
    const next = Math.min(Math.max(i + delta, 0), ids.length - 1);
    if (next !== i) get().select(ids[next]);
  },

  loadFirstPage: async () => {
    get().reset();
    return get()._loadPage(null, "first");
  },

  /**
   * @param {'scroll'|'first'|'manual'} trigger why this page was requested.
   *   Unused in 3a — the only consumer is a list. It is the seam for
   *   prefetch-ahead in 3c, which will want to distinguish a page the user
   *   asked for from one fetched speculatively. Not built now: speculative
   *   prefetch has nothing to speculate about while the only consumer is a
   *   list the user is already scrolling.
   */
  loadNextPage: async (trigger = "scroll") => {
    const { cursor, hasMore, loading } = get();
    if (!hasMore || loading) return;
    return get()._loadPage(cursor, trigger);
  },

  _loadPage: async (cursor, _trigger) => {
    const { filters } = get();
    set({ loading: true, error: null });
    try {
      const d = await getJson(buildQueueUrl(filters, cursor));
      const items = d.items ?? EMPTY_ARRAY;

      // An empty page is the END OF THE QUEUE, not an error and not a spinner.
      // The server sets next_cursor whenever rows.length === limit, so a queue
      // whose total is an exact multiple of the page size yields one final
      // empty page. See backlog item 10 — this convention cannot distinguish
      // "exhausted" from "everything on this page was filtered out", which is
      // safe today only because no filter can remove rows after selection.
      if (items.length === 0) {
        set({ loading: false, hasMore: false, cursor: null });
        return;
      }

      set((s) => {
        const seen = new Set(s.ids);
        const fresh = items.filter((i) => !seen.has(i.id));
        const records = { ...s.records };
        for (const i of items) records[i.id] = i;
        return {
          ids: fresh.length ? [...s.ids, ...fresh.map((i) => i.id)] : s.ids,
          records,
          cursor: d.next_cursor ?? null,
          hasMore: Boolean(d.next_cursor),
          loading: false,
        };
      });
    } catch (err) {
      set({ loading: false, error: err });
    }
  },

  /**
   * Changing sort or tiers MUST reset the working set before fetching. The
   * cursor encodes a tier ordinal relative to the sort it was issued under, so
   * carrying one across a sort change does not error — it silently pages into
   * the wrong place. Status is included for the same reason even though it
   * cannot currently discriminate.
   */
  setFilters: (patch) => {
    const prev = get().filters;
    const next = { ...prev, ...patch };
    // EVERY FILTER FIELD HAS TO BE COMPARED HERE. A field added to
    // DEFAULT_FILTERS and to buildQueueUrl but not to this line is a filter the
    // UI accepts, stores, and never applies: setFilters returns before
    // loadFirstPage, so the list keeps showing the previous result and nothing
    // errors. That is silent, and it is the most likely way this store breaks.
    const changed =
      next.sort !== prev.sort ||
      next.tiers.join(",") !== prev.tiers.join(",") ||
      next.statuses.join(",") !== prev.statuses.join(",") ||
      next.aoiId !== prev.aoiId;
    if (!changed) return;

    // ── RELEASE CLAIMS WHEN THE AOI MOVES ──────────────────────────────────
    //
    // A claim is a reservation on work you are about to do. Change AOI and that
    // stops being true: the records claimed under the old one are not work this
    // reviewer is going to reach, but wellfinder_record_claims has no AOI
    // dimension, so they stay locked to EVERY OTHER analyst for the full
    // 15-minute TTL.
    //
    // Not fired for tier or status changes, which stay within the same body of
    // work — this is specifically about switching to a different assignment.
    // Fire-and-forget: releaseClaims is already best-effort, and the TTL is the
    // real guarantee.
    if (next.aoiId !== prev.aoiId) get().releaseClaims();

    set({ filters: next });
    get().loadFirstPage();

    // ── RE-ARM CLAIMING WHEN THE FILTER COMES BACK TO PENDING ──────────────
    //
    // claimBatch ran on page mount and on advance()'s low-water mark, and
    // nowhere else. Both are reachable only from the ordinary pending queue, so
    // once an analyst switched the status chips away and back, NOTHING re-armed
    // claiming for the rest of the session: the mount had already happened, and
    // advance() tops up a working set it never got to refill.
    //
    // Fired on the TRANSITION back, not on every setFilters. Re-claiming is
    // idempotent for the same caller — the server's ON CONFLICT predicate
    // includes claimed_by = EXCLUDED.claimed_by — but claiming on every tier
    // chip would spend a round trip out of the three-second budget between
    // records for nothing.
    //
    // Fire-and-forget, like the mount call: a failed claim costs contention,
    // not correctness.
    const backToPending =
      next.statuses.join(",") === "pending"
      && prev.statuses.join(",") !== "pending";
    if (backToPending) get().claimBatch();

    // ── STATS RELOAD ON AOI CHANGE ONLY ────────────────────────────────────
    //
    // The header has to move with the list: 10,800 above a list of 4,647 is
    // worse than no filter, because the reviewer cannot tell how much of the
    // assigned work is left.
    //
    // But ONLY for the AOI. The header describes the corpus, not the page — a
    // tier or status chip does not move a number on it, so reloading there
    // would flicker an orientation surface for no change, which the page's own
    // comment already called out before this filter existed. Reloading
    // unconditionally would have quietly reversed that decision.
    if (next.aoiId !== prev.aoiId) get().loadStats();
  },

  loadStats: async () => {
    set({ statsLoading: true, statsError: null });
    try {
      const { aoiId } = get().filters;
      const stats = await getJson(
        `/api/v1/wellfinder/stats${aoiId ? `?operating_aoi_id=${encodeURIComponent(aoiId)}` : ""}`,
      );
      set({ stats, statsLoading: false });
    } catch (err) {
      set({ statsLoading: false, statsError: err });
    }
  },
}));
